/**
 * waitElapsed — the one place that turns "how long since X" into a number or a label.
 *
 * WHY THIS FILE EXISTS (2026-09-21). Practice Pulse computed elapsed time inline, twice, and
 * compared the result against a minute threshold:
 *
 *     diffMinutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));   // 0..59
 *     if (diffMinutes >= practicePulseSettings.elapsedTime) { ...fire the alert... }
 *
 * `diffMinutes` is minutes WITHIN THE HOUR. That is correct for DISPLAY, where hours are printed
 * separately ("1 hours, 5 minute(s)"), and wrong for a COMPARISON, where it silently resets every
 * hour: a 15-minute threshold stopped firing between 60 and 74 minutes, and any threshold of 60 or
 * more could never fire at all. The waiting-limit banner was commented out in the template with
 * "hiding this for now COZ IT'S NOT WORKING sometimes" — an accurate bug report.
 *
 * So: display keeps the hour split, comparisons use TOTAL minutes, and both live here where a test
 * can reach them. `ShowPracticePulseDetails.vue` already used moment's `.diff(x, 'minutes')` (total
 * minutes) against the SAME setting, so the report and the live board disagreed about the same
 * patient. This removes that disagreement.
 *
 * ⚠️ Keep this file import-free. The unit tests execute this exact file, and it is deliberately
 *    free of moment/Vue so it stays cheap to test — same rule as src/utils/phiMask.js.
 *
 * ⛔ The two format functions reproduce what the product renders TODAY, wart included:
 *    `minute(s)` with the literal "(s)" on alert cards, and "hours" with no singular. That text is
 *    on screen in front of customers and in published material; tidying it here would make the UI
 *    disagree with itself. The ONE thing fixed is the hour boundary — see formatAlertElapsed.
 */

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;

/**
 * Milliseconds between `start` and `now`, or null when `start` is unusable.
 * Accepts a Date, a millisecond number, or a Firestore Timestamp (anything with .toDate()).
 * Never throws: PracticePulse dereferenced `dateTimeReady.toDate()` with no guard, and one READY
 * patient without that field took down the whole Firestore snapshot loop.
 */
export function elapsedMs(start, now = Date.now()) {
  if (start == null) return null;

  let startMs;
  if (typeof start === 'number') startMs = start;
  else if (start instanceof Date) startMs = start.getTime();
  else if (typeof start.toDate === 'function') {
    const d = start.toDate();
    startMs = d instanceof Date ? d.getTime() : NaN;
  } else if (typeof start === 'string') startMs = Date.parse(start);
  else return null;

  if (!Number.isFinite(startMs)) return null;

  const nowMs = now instanceof Date ? now.getTime() : now;
  if (!Number.isFinite(nowMs)) return null;

  const diff = nowMs - startMs;
  return diff < 0 ? 0 : diff;   // a clock skew must never read as "waiting a negative time"
}

/** TOTAL minutes elapsed. This is the only correct input to a threshold comparison. */
export function totalMinutes(ms) {
  if (!Number.isFinite(ms) || ms < 0) return null;
  return Math.floor(ms / MS_PER_MINUTE);
}

/** Hours plus minutes-within-the-hour. This is the only correct input to a DISPLAY string. */
export function splitHoursMinutes(ms) {
  if (!Number.isFinite(ms) || ms < 0) return null;
  return {
    hours: Math.floor(ms / MS_PER_HOUR),
    minutes: Math.floor((ms % MS_PER_HOUR) / MS_PER_MINUTE),
  };
}

/**
 * Has this wait reached the configured limit?
 *
 * `thresholdMinutes` arrives from teamio.settings PRACTICE_PULSE_SETTINGS as JSON, where it has
 * been seen stored as the STRING "5" as well as a number, so it is coerced here rather than at
 * each call site. A missing, zero, blank or non-numeric threshold means "not configured" and
 * returns false — never a fire.
 */
export function isOverThreshold(ms, thresholdMinutes) {
  const mins = totalMinutes(ms);
  if (mins === null) return false;
  const limit = Number(thresholdMinutes);
  if (!Number.isFinite(limit) || limit <= 0) return false;
  return mins >= limit;
}

/**
 * The ALERT CARD label — "less than a minute" / "5 minute(s)" / "1 hours, 5 minute(s)".
 *
 * 🔴 THE FIX: the old inline version wrote the hour part, then unconditionally overwrote the whole
 * string with "less than a minute" whenever minutes-within-the-hour was 0. So an alert standing
 * for EXACTLY 1, 2 or 3 hours displayed "less than a minute" — the clock lying in the one
 * direction that matters, on the feature whose entire promise is "a clock tells you nobody came."
 * Now "less than a minute" is reserved for a genuinely sub-minute wait.
 */
export function formatAlertElapsed(ms) {
  const hm = splitHoursMinutes(ms);
  if (hm === null) return '';
  const { hours, minutes } = hm;

  if (hours === 0 && minutes === 0) return 'less than a minute';

  let out = '';
  if (hours > 0) out = `${hours} hours`;
  if (minutes > 0) out = out ? `${out}, ${minutes} minute(s)` : `${minutes} minute(s)`;
  return out;
}

/**
 * The PATIENT CARD label — "" / "5 minutes" / "1 hours, 5 minutes".
 *
 * Returns '' below one minute, which is what the template already relies on: the "Waiting:" badge
 * is gated on this value being truthy, so it stays hidden in the first minute. The fix here is the
 * same hour boundary — the old version produced the dangling "1 hours, " at exactly the hour.
 */
export function formatPatientElapsed(ms) {
  const hm = splitHoursMinutes(ms);
  if (hm === null) return '';
  const { hours, minutes } = hm;

  let out = '';
  if (hours > 0) out = `${hours} hours`;
  if (minutes > 0) out = out ? `${out}, ${minutes} minutes` : `${minutes} minutes`;
  return out;
}

/**
 * Is the waiting-limit feature switched on for this site?
 *
 * ⚠️ BOTH halves are required and the runtime previously read only the second. ConfigurePracticePulse
 * disables the minutes input when the checkbox is off but does NOT clear the stored number, so a
 * site that turned the feature off kept an orphaned threshold and the board kept firing on it.
 * Measured on prod 2026-09-21: 0 sites are currently in that state, so this closes a latent trap
 * rather than fixing live behaviour.
 */
export function waitAlertEnabled(settings) {
  if (!settings) return false;
  if (settings.elapsedTimeAlert !== 'TRUE') return false;
  const limit = Number(settings.elapsedTime);
  return Number.isFinite(limit) && limit > 0;
}
